/**
 * StatusBar.tsx — the bottom VSCode-style toggle rail.
 *
 * Replaces the old View menu in the MenuBar. Every view toggle that used to
 * live behind View → (Side panel / Groups / Tables / Projections / Grid /
 * Arrows / Theme) is now a single icon toggle button here, plus the new
 * Minimap toggle. The bar also carries the run status on its right edge:
 * step counter, a colored status dot, an optional connection dot, and — in
 * relay mode — a pulsing "listening" indicator.
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ [◧][⊡][▦][◉][#][→][⬡][☀]            step 42 ●  ● connected │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Buttons are flat icon-only toggles; the pressed (active) state fills the
 * background so the user can read the panel layout at a glance. Tooltips
 * carry the label for discoverability.
 */
import type { ConnectionStatus } from '../engine/ApiAdapter';

interface StatusBarProps {
  // Panel + canvas toggles (each is a push-toggle; pressed = visible/on).
  showGroupsPanel: boolean;
  onToggleGroupsPanel: () => void;
  showTables: boolean;
  onToggleTables: () => void;
  showOverlays: boolean;
  onToggleOverlays: () => void;
  showGrid: boolean;
  onToggleGrid: () => void;
  showArrows: boolean;
  onToggleArrows: () => void;
  showMinimap: boolean;
  onToggleMinimap: () => void;
  // Right-side status.
  status: 'idle' | 'running' | 'stopped';
  step: number;
  /** API connection state. When set, a connection dot + label render. */
  connectionStatus?: ConnectionStatus;
  apiLabel?: string;
  /** Relay mode — an external model POSTs ticks. Shows a pulsing dot. */
  relay?: boolean;
  /** Open the agent decisions log modal (the DecisionsModal). */
  onOpenDecisions?: () => void;
  /** Open the ⚡ workflow-triggers modal ("Triggers"). */
  onOpenTriggers?: () => void;
  /** Open the global-agents panel. */
  onOpenAgents?: () => void;
  /** Open the Services modal (running docker containers). */
  onOpenServices?: () => void;
  /** Open the matrix codegen modal (graph → incidence/adjacency/Laplacian
   *  → NumPy, for linear-programming setups). */
  onOpenMatrixCode?: () => void;
}

interface ToggleProps {
  label: string;
  pressed: boolean;
  onClick: () => void;
  icon: JSX.Element;
}

function Toggle({ label, pressed, onClick, icon }: ToggleProps): JSX.Element {
  return (
    <button
      type="button"
      className={`status-toggle${pressed ? ' pressed' : ''}`}
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={pressed}
    >
      {icon}
    </button>
  );
}

export function StatusBar(props: StatusBarProps): JSX.Element {
  return (
    <footer className="status-bar">
      <div className="status-toggles">
        {props.onOpenServices && (
          <button
            type="button"
            className="status-toggle"
            onClick={props.onOpenServices}
            title="Services"
            aria-label="Services"
          >
            <DockerIcon />
          </button>
        )}
        {props.onOpenTriggers && (
          <button
            type="button"
            className="status-toggle"
            onClick={props.onOpenTriggers}
            title="Workflow triggers"
            aria-label="Workflow triggers"
          >
            <BoltIcon />
          </button>
        )}
        {props.onOpenAgents && (
          <button
            type="button"
            className="status-toggle"
            onClick={props.onOpenAgents}
            title="Global agents"
            aria-label="Global agents"
          >
            <AgentsIcon />
          </button>
        )}
      </div>
      <div className="status-center" style={{
        gap: 2,
        display: 'flex' 
      }}>
        <Toggle
          label="Tables panel"
          pressed={props.showTables}
          onClick={props.onToggleTables}
          icon={<TablesIcon />}
        />
        
        <Toggle
          label="Projections"
          pressed={props.showOverlays}
          onClick={props.onToggleOverlays}
          icon={<ProjectionsIcon />}
        />
        <Toggle
          label="Grid"
          pressed={props.showGrid}
          onClick={props.onToggleGrid}
          icon={<GridIcon />}
        />
        <Toggle
          label="Arrows"
          pressed={props.showArrows}
          onClick={props.onToggleArrows}
          icon={<ArrowsIcon />}
        />
        <Toggle
          label="Minimap"
          pressed={props.showMinimap}
          onClick={props.onToggleMinimap}
          icon={<MinimapIcon />}
        />
      </div>
      <div className="status-right">
        {props.relay && (
          <span
            className="status-relay"
            title={
              props.connectionStatus === 'error'
                ? 'Relay: connection error — reconnect to resume.'
                : props.connectionStatus === 'connected'
                  ? 'Listening for events'
                  : 'Relay: connecting…'
            }
            aria-label="Relay status"
          >
            <span className="relay-pulse-mini" aria-hidden />
          </span>
        )}
        
        {/* <span
          className={`status-dot status-${props.status}`}
          title={`Status: ${props.status}`}
          aria-label={`Status: ${props.status}`}
        >
          ●
        </span> */}
        {/* {props.apiLabel && (
          <span
            className={`status-conn conn-${props.connectionStatus ?? 'disconnected'}`}
            title={`API: ${props.connectionStatus ?? 'disconnected'} — ${props.apiLabel}`}
            aria-label={`Connection: ${props.connectionStatus ?? 'disconnected'}`}
          >
            ●
          </span>
        )} */}
        <span className="status-step" title='Tick'>{props.step}</span>
        {props.onOpenMatrixCode && (
          <button
            type="button"
            className="status-toggle"
            onClick={props.onOpenMatrixCode}
            title="Matrix codegen"
            aria-label="Matrix codegen"
          >
            <MatrixCodeIcon />
          </button>
        )}
        {props.onOpenDecisions && (
          <button
            type="button"
            className="status-toggle"
            onClick={props.onOpenDecisions}
            title="Agent activity log"
            aria-label="Agent activity log"
          >
            <DecisionsIcon />
          </button>
        )}
        <Toggle
          label="Groups panel"
          pressed={props.showGroupsPanel}
          onClick={props.onToggleGroupsPanel}
          icon={<GroupsIcon />}
        />
      </div>
    </footer>
  );
}

// ─── Inline icons (16px, currentColor) ────────────────────────────────────
// Hand-rolled SVGs so the bar doesn't pull a dozen phosphor imports; each is
// a recognizable single-glyph shape. Stroke = currentColor so the toggle's
// pressed/hover states drive the color via CSS.



function GroupsIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="2" y="2.5" width="5" height="5" rx="1" />
      <rect x="9" y="2.5" width="5" height="5" rx="1" />
      <rect x="2" y="9" width="5" height="4.5" rx="1" />
      <rect x="9" y="9" width="5" height="4.5" rx="1" />
    </svg>
  );
}

/** A lightning bolt — the workflow-triggers glyph (matches the canvas's ⚡
 *  trigger badge idiom). */
function BoltIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round">
      <path d="M9 1.5 3.5 9H7l-1 5.5L11.5 7H8L9 1.5Z" />
    </svg>
  );
}

/** A robot head — the global-agents glyph. */
function AgentsIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="3" y="5" width="10" height="7.5" rx="2" />
      <circle cx="6" cy="8.75" r="1" fill="currentColor" stroke="none" />
      <circle cx="10" cy="8.75" r="1" fill="currentColor" stroke="none" />
      <line x1="8" y1="5" x2="8" y2="2.5" />
      <circle cx="8" cy="2" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** A docker-container glyph — stacked containers (the Services button).
 *  Exported: the start page's top-right services affordance reuses it. */
export function DockerIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
      <rect x="1.5" y="9" width="3.4" height="3" />
      <rect x="6.3" y="9" width="3.4" height="3" />
      <rect x="11.1" y="9" width="3.4" height="3" />
      <rect x="1.5" y="4.5" width="3.4" height="3" />
      <rect x="6.3" y="4.5" width="3.4" height="3" />
      <rect x="11.1" y="4.5" width="3.4" height="3" />
      <path d="M8 4.5V2.2c2.6 0 4.3.9 5 2.3" strokeLinecap="round" />
    </svg>
  );
}

/** A log list glyph — bullet lines, reading as "entries in a log". */
function DecisionsIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <circle cx="3" cy="4" r="1" fill="currentColor" stroke="none" />
      <line x1="6" y1="4" x2="13.5" y2="4" />
      <circle cx="3" cy="8" r="1" fill="currentColor" stroke="none" />
      <line x1="6" y1="8" x2="13.5" y2="8" />
      <circle cx="3" cy="12" r="1" fill="currentColor" stroke="none" />
      <line x1="6" y1="12" x2="11.5" y2="12" />
    </svg>
  );
}

/** A bracketed 2×2 grid — the matrix-codegen glyph (reads as both "matrix"
 *  and "code": square brackets around cells). */
function MatrixCodeIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
      <path d="M4.5 2.5H3a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h1.5" />
      <path d="M11.5 2.5H13a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-1.5" />
      <line x1="5.5" y1="5.5" x2="7.5" y2="5.5" />
      <line x1="9" y1="5.5" x2="10.5" y2="5.5" />
      <line x1="5.5" y1="8" x2="6" y2="8" />
      <line x1="7.5" y1="8" x2="10.5" y2="8" />
      <line x1="5.5" y1="10.5" x2="7.5" y2="10.5" />
    </svg>
  );
}

function TablesIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
      <line x1="2" y1="6" x2="14" y2="6" />
      <line x1="2" y1="9.5" x2="14" y2="9.5" />
      <line x1="6" y1="2.5" x2="6" y2="13.5" />
    </svg>
  );
}
function ProjectionsIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <circle cx="8" cy="8" r="5.5" />
      <circle cx="8" cy="8" r="2" fill="currentColor" stroke="none" />
    </svg>
  );
}
function GridIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <line x1="2" y1="5.5" x2="14" y2="5.5" />
      <line x1="2" y1="10.5" x2="14" y2="10.5" />
      <line x1="5.5" y1="2" x2="5.5" y2="14" />
      <line x1="10.5" y1="2" x2="10.5" y2="14" />
    </svg>
  );
}
function ArrowsIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <line x1="2.5" y1="8" x2="13" y2="8" />
      <polyline points="9.5,4.5 13,8 9.5,11.5" />
    </svg>
  );
}
function MinimapIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
      <rect x="4.5" y="5" width="4" height="3" fill="currentColor" stroke="none" />
      <rect x="9" y="8.5" width="3" height="3" fill="currentColor" stroke="none" opacity="0.6" />
    </svg>
  );
}
